import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Menu,
  MenuItem,
  Paper,
} from '@mui/material';
import { DataTable } from '../../../../../ui';
import { getColumns } from './Functions/GetColumns';
import { sortCandidates } from '../../../../../utils/types/JobOpenings';
import { SearchIcons } from '../../../../../icons/icons';
import { SearchIcon } from '../../../../../ui/icons/icons';
import { getPsychometricApplicants, updateJobApplicantStatus } from '../../../../../utils/api_service/JobOpenings';
import { useParams } from 'react-router-dom';

export interface CandidateListType {
  name: string;
  email: string;
  id: string;
  psychometricStatus: string;
  applicantStatus: string;
  invited: boolean;
}

// ---------- Debounce hook (must be outside component) ----------

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// ---------- Minimal dependency-free XLSX writer (stored/uncompressed ZIP) ----------

const crc32Table = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
    }
    table[n] = c;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc = crc32Table[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function strToBytes(str: string): Uint8Array {
  return new TextEncoder().encode(str);
}

function dosDateTime(date: Date) {
  const time =
    ((date.getHours() & 0x1f) << 11) |
    ((date.getMinutes() & 0x3f) << 5) |
    ((Math.floor(date.getSeconds() / 2)) & 0x1f);
  const dosDate =
    (((date.getFullYear() - 1980) & 0x7f) << 9) |
    (((date.getMonth() + 1) & 0x0f) << 5) |
    (date.getDate() & 0x1f);
  return { time, dosDate };
}

interface ZipEntry {
  name: string;
  data: Uint8Array;
}

function buildZip(files: { name: string; content: string }[]): Blob {
  const now = new Date();
  const { time, dosDate } = dosDateTime(now);
  const entries: ZipEntry[] = files.map((f) => ({ name: f.name, data: strToBytes(f.content) }));

  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    const nameBytes = strToBytes(entry.name);
    const crc = crc32(entry.data);
    const size = entry.data.length;

    const localHeader = new DataView(new ArrayBuffer(30));
    localHeader.setUint32(0, 0x04034b50, true);
    localHeader.setUint16(4, 20, true);
    localHeader.setUint16(6, 0, true);
    localHeader.setUint16(8, 0, true);
    localHeader.setUint16(10, time, true);
    localHeader.setUint16(12, dosDate, true);
    localHeader.setUint32(14, crc, true);
    localHeader.setUint32(18, size, true);
    localHeader.setUint32(22, size, true);
    localHeader.setUint16(26, nameBytes.length, true);
    localHeader.setUint16(28, 0, true);

    const localChunk = new Uint8Array(30 + nameBytes.length + entry.data.length);
    localChunk.set(new Uint8Array(localHeader.buffer), 0);
    localChunk.set(nameBytes, 30);
    localChunk.set(entry.data, 30 + nameBytes.length);
    localParts.push(localChunk);

    const centralHeader = new DataView(new ArrayBuffer(46));
    centralHeader.setUint32(0, 0x02014b50, true);
    centralHeader.setUint16(4, 20, true);
    centralHeader.setUint16(6, 20, true);
    centralHeader.setUint16(8, 0, true);
    centralHeader.setUint16(10, 0, true);
    centralHeader.setUint16(12, time, true);
    centralHeader.setUint16(14, dosDate, true);
    centralHeader.setUint32(16, crc, true);
    centralHeader.setUint32(20, size, true);
    centralHeader.setUint32(24, size, true);
    centralHeader.setUint16(28, nameBytes.length, true);
    centralHeader.setUint16(30, 0, true);
    centralHeader.setUint16(32, 0, true);
    centralHeader.setUint16(34, 0, true);
    centralHeader.setUint16(36, 0, true);
    centralHeader.setUint32(38, 0, true);
    centralHeader.setUint32(42, offset, true);

    const centralChunk = new Uint8Array(46 + nameBytes.length);
    centralChunk.set(new Uint8Array(centralHeader.buffer), 0);
    centralChunk.set(nameBytes, 46);
    centralParts.push(centralChunk);

    offset += localChunk.length;
  }

  const centralDirSize = centralParts.reduce((sum, p) => sum + p.length, 0);
  const centralDirOffset = offset;

  const endRecord = new DataView(new ArrayBuffer(22));
  endRecord.setUint32(0, 0x06054b50, true);
  endRecord.setUint16(4, 0, true);
  endRecord.setUint16(6, 0, true);
  endRecord.setUint16(8, entries.length, true);
  endRecord.setUint16(10, entries.length, true);
  endRecord.setUint32(12, centralDirSize, true);
  endRecord.setUint32(16, centralDirOffset, true);
  endRecord.setUint16(20, 0, true);

  const allParts = [...localParts, ...centralParts, new Uint8Array(endRecord.buffer)];
  return new Blob(allParts, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function colName(index: number): string {
  let name = '';
  let n = index + 1;
  while (n > 0) {
    const rem = (n - 1) % 26;
    name = String.fromCharCode(65 + rem) + name;
    n = Math.floor((n - 1) / 26);
  }
  return name;
}

function buildSheetXml(rows: (string | number)[][]): string {
  const rowXml = rows.map((row, rIdx) => {
    const cells = row.map((cell, cIdx) => {
      const ref = `${colName(cIdx)}${rIdx + 1}`;
      if (typeof cell === 'number' && !isNaN(cell)) {
        return `<c r="${ref}"><v>${cell}</v></c>`;
      }
      const text = escapeXml(String(cell ?? ''));
      return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${text}</t></is></c>`;
    }).join('');
    return `<row r="${rIdx + 1}">${cells}</row>`;
  }).join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<sheetData>${rowXml}</sheetData>
</worksheet>`;
}

function createXlsxBlob(rows: (string | number)[][]): Blob {
  const sheetXml = buildSheetXml(rows);

  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
</Types>`;

  const rootRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

  const workbookXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets><sheet name="Candidates" sheetId="1" r:id="rId1"/></sheets>
</workbook>`;

  const workbookRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
</Relationships>`;

  return buildZip([
    { name: '[Content_Types].xml', content: contentTypes },
    { name: '_rels/.rels', content: rootRels },
    { name: 'xl/workbook.xml', content: workbookXml },
    { name: 'xl/_rels/workbook.xml.rels', content: workbookRels },
    { name: 'xl/worksheets/sheet1.xml', content: sheetXml },
  ]);
}

// ---------- Component ----------

export const Config: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [candidatesList, setCandidatesList] = useState<CandidateListType[]>([]);
  const [sort, setSort] = useState<sortCandidates>({ firstName: 'asc' });
  const [localName, setLocalName] = useState('');
  const [localEmail, setLocalEmail] = useState('');
  const [anchorElExport, setAnchorElExport] = useState<null | HTMLElement>(null);
  const [jobLevel, setJobLevel] = useState<'Staff' | 'Manager'>('Staff');
  const { id } = useParams<{ id: string }>();

  const debouncedName = useDebounce(localName, 400);
  const debouncedEmail = useDebounce(localEmail, 400);

  const onPageChange = (page: number) => {
    setPageNumber(page);
  };

  const handleRetake = (row: any) => {
    console.log('Remove invite', row);
  };

  // Reset to page 1 when search terms change
  useEffect(() => {
    setPageNumber(1);
  }, [debouncedName, debouncedEmail]);

  // Fetch when page or debounced search values change
  useEffect(() => {
    getApplicants();
  }, [debouncedName, debouncedEmail, pageNumber]);

  const handleOpenExport = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElExport(event.currentTarget);
  };

  const handleCloseExport = () => {
    setAnchorElExport(null);
  };

  const getApplicants = async () => {
    setLoading(true);
    try {
      const response = await getPsychometricApplicants(
        id,
        pageNumber,
        30,
        debouncedName,
        debouncedEmail,
      );

      if (response.ok) {
        const data = await response.json();
        setCandidatesList(data.results ?? []);
        setTotalPages(Math.ceil(data.total_count / 30));
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  const handleSelectRow = (_rowIndex: number, row: CandidateListType) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(row.id)) {
        next.delete(row.id);
      } else {
        next.add(row.id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedRows.size === candidatesList.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(candidatesList.map((c) => c.id)));
    }
  };

  const getExportRows = () => {
    const selected = candidatesList.filter((c) => selectedRows.has(c.id));
    const headers = ['ID', 'Email', 'Full Name', 'Job Level'];
    const rows = selected.map((c) => [
      c.id,
      c.email,
      c.name,
      jobLevel,
    ]);
    return { headers, rows };
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const updateSelectedToPending = async () => {
    const selected = candidatesList.filter((c) => selectedRows.has(c.id));
    if (!selected.length) return;

    try {
      await updateJobApplicantStatus(id!, {
        jobApplicantIds: selected.map((c) => c.id),
        applicantStatus: 'Psychometric-Inprogress',
      });
      await getApplicants(); // refresh the table
    } catch (e) {
      console.error('Failed to update applicant statuses', e);
    }
  };

  const handleExportCSV = async () => {
    const { headers, rows } = getExportRows();
    if (!rows.length) return;

    const today = new Date().toISOString().split('T')[0];
    const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, `candidates_${today}.csv`);
    handleCloseExport();
    await updateSelectedToPending();
  };

  const handleExportXLSX = async () => {
    const { headers, rows } = getExportRows();
    if (!rows.length) return;

    const today = new Date().toISOString().split('T')[0];
    const blob = createXlsxBlob([headers, ...rows]);
    downloadBlob(blob, `candidates_${today}.xlsx`);
    handleCloseExport();
    await updateSelectedToPending();
  };

  const isRowSelected = (_rowIndex: number, row: CandidateListType) =>
    selectedRows.has(row.id);

  const isAllSelected = selectedRows.size === candidatesList.length;
  const isIndeterminate = selectedRows.size > 0 && selectedRows.size < candidatesList.length;

  const handleSortChange = (key: string, direction: 'asc' | 'desc') => {
    setSort((prev) => {
      const next = { ...prev };
      if (next[key as keyof sortCandidates] === direction) {
        delete next[key as keyof sortCandidates];
      } else {
        next[key as keyof sortCandidates] = direction;
      }
      return next;
    });
    setPageNumber(1);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper
        elevation={0}
        sx={{
          bgcolor: 'grey.50',
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 3,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        {/* Left: search + level toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField
            size='small'
            placeholder='Search by name'
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            sx={{ width: 180 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon size={16} color="#9ca3af" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            size='small'
            placeholder='Search by email'
            value={localEmail}
            onChange={(e) => setLocalEmail(e.target.value)}
            sx={{ width: 180 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcons size={16} color="#9ca3af" />
                </InputAdornment>
              ),
            }}
          />

          {/* Segmented job level toggle */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 2,
              p: 0.5,
              gap: 0.5,
              height: 40,
            }}
          >
            {(['Staff', 'Manager'] as const).map((level) => (
              <Button
                key={level}
                size='small'
                variant={jobLevel === level ? 'contained' : 'text'}
                onClick={() => setJobLevel(level)}
                sx={{
                  minWidth: 0,
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  borderRadius: 1.5,
                  ...(jobLevel === level
                    ? { bgcolor: 'primary.main', color: 'common.white' }
                    : { color: 'text.secondary', '&:hover': { color: 'text.primary' } }),
                }}
              >
                {level}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Right: actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant='contained'
            sx={{ borderRadius: 2, textTransform: 'none', height: 40 }}
          >
            Send invites
          </Button>

          <Button
            variant='contained'
            onClick={handleOpenExport}
            sx={{ borderRadius: 2, textTransform: 'none', height: 40 }}
          >
            Export &#x25BE;
          </Button>
          <Menu
            anchorEl={anchorElExport}
            open={Boolean(anchorElExport)}
            onClose={handleCloseExport}
          >
            <MenuItem onClick={handleExportCSV}>Export as CSV</MenuItem>
            <MenuItem onClick={handleExportXLSX}>Export as XLSX</MenuItem>
          </Menu>
        </Box>
      </Paper>

      {/* Job level hint */}
      <Typography variant='caption' sx={{ color: 'text.secondary', px: 1, mt: -1 }}>
        Job level{' '}
        <Typography component='span' variant='caption' sx={{ fontWeight: 600, color: 'primary.main' }}>
          {jobLevel}
        </Typography>{' '}
        will be included in exports.
      </Typography>

      {candidatesList.length ? (
        <DataTable
          isLoading={loading}
          columns={getColumns(handleRetake)}
          data={candidatesList}
          totalRecords={candidatesList.length}
          rowsPerPage={10}
          onPageChange={onPageChange}
          currentPage={pageNumber}
          totalPages={totalPages}
          onSortChange={handleSortChange}
          sortConfig={Object.entries(sort).map(([key, dir]) => ({
            key,
            direction: dir,
          }))}
          hasCheckBox
          onClickRow={(row) => handleSelectRow(-1, row)}
          onSelectAll={handleSelectAll}
          onSelectRow={handleSelectRow}
          isRowSelected={isRowSelected}
          isAllSelected={isAllSelected}
          isIndeterminate={isIndeterminate}
        />
      ) : (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <Typography variant='h6' sx={{ color: 'text.primary' }}>
            No Applicants
          </Typography>
        </Box>
      )}
    </Box>
  );
};